import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { Subscription } from 'rxjs';
import { Theme } from '../../../core/models/theme.model';
import { select, Store } from '@ngrx/store';
import * as ConfigActions from '../../../root-store/config/actions/config.actions';
import * as fromConfig from '../../../root-store/config/selectors/config.selectors';
import { TranslateService } from '@ngx-translate/core';
import * as fromLocale from 'src/app/root-store/locale/locale.selectors';
import { map, mergeMap } from 'rxjs/operators';
import { DomSanitizer } from '@angular/platform-browser';
import { KeycloakService } from 'keycloak-angular';
import CONSTANTS from '../../../../@utility/constants';
import { StorageService } from '../../../core/service/storage.service';

@Component({
  selector: 'app-menu',
  templateUrl: './menu.component.html',
  styleUrls: ['./menu.component.scss'],
})
export class MenuComponent implements OnInit {
  config: Theme;
  subscription: Subscription;
  items = [];
  @Output() menuToggleClick: EventEmitter<any> = new EventEmitter();
  @Input() active: boolean;
  @Input() fullmenu: boolean;
  tooTipDirection: string;
  menuClosed: boolean;
  menuPanel: boolean;
  labels = [];
  logoUrl: any = 'assets/images/app-logo.png';
  loading = false;

  constructor(
    private store: Store,
    public translate: TranslateService,
    private _sanitizer: DomSanitizer,
    private keycloakService: KeycloakService,
    private storage: StorageService
  ) {}

  ngOnInit(): void {
    this.loading = true;
    this.tooTipDirection = 'left';
    this.menuClosed = false;
    this.menuPanel = false;
    this.store.dispatch(ConfigActions.loadConfig());

    this.store
      .pipe(
        select(fromLocale.getMenu),
        mergeMap((menu) => {
          return this.store.pipe(
            select(fromLocale.getLocaleLabels),
            map((labels) => ({ labels, menu }))
          );
        })
      )
      .subscribe(
        ({ menu, labels }) => {
          this.loadLogo();
          this.labels = labels;
          const items = menu ? this.getMenus(menu) : this.items;

          if (labels) {
            this.loading = false;
            this.items = this.applyLabels(items);
            const appKeyValues = this.storage.getItem(
              CONSTANTS.appKeyValuesStoreKey
            );

            this.storage.setItem(CONSTANTS.appKeyValuesStoreKey, {
              ...appKeyValues,
              APP_MENU: JSON.stringify(this.removeUndefinedItems(this.items)),
            });
          }
        },
        () => {
          this.loading = false;
        }
      );
    this.store.pipe(select(fromConfig.getConfig)).subscribe((config) => {
      this.config = config;
      this.resetMenu();
    });
  }

  getMenus(rawMenu) {
    const roles = this.keycloakService.getUserRoles(true);
    return this.filterMenu(JSON.parse(rawMenu), roles);
  }

  filterMenu(menus, roles) {
    const filteredMenus = [];
    for (const menu of menus) {
      let includeMenu = menu;
      if (menu.roles && menu.roles instanceof Array && menu.roles.length) {
        if (!menu.roles.some((role) => roles.includes(role))) {
          includeMenu = null;
        }
      }

      if (includeMenu) {
        let items = void 0;
        if (includeMenu.items && includeMenu.items.length) {
          items = this.filterMenu(includeMenu.items, roles);
        }
        filteredMenus.push({ ...includeMenu, items });
      }
    }
    return filteredMenus;
  }

  loadLogo() {
    this.store.select(fromLocale.getLogo).subscribe((logo) => {
      if (logo) {
        this.logoUrl = this._sanitizer.bypassSecurityTrustResourceUrl(logo);
      }
    });
  }

  applyLabels(items) {
    return (
      items &&
      items.map((item) => {
        item.label = this.labels['data'][item.labelId]?.value;
        if ('items' in item) {
          item.items = this.applyLabels(item['items']);
        }
        return item;
      })
    );
  }

  getClass(): any {
    return { 'close-menu': this.menuPanel, hide: this.menuClosed };
  }

  menuClass(): any {
    if (!this.config || this.config.direction === 'ltr') {
      return {
        active: this.active,
        'slide-in-ltr': this.menuClosed,
        'sidebar-right': false,
        'sidebar-left': true,
        'full-menu': this.fullmenu,
      };
    } else {
      return {
        active: this.active,
        'full-menu': this.fullmenu,
        'slide-in-rtl': this.menuClosed,
        'sidebar-right': true,
        'sidebar-left': false,
      };
    }
  }

  resetMenu(): void {
    this.menuToggleClick.emit({
      direction: this.config ? this.config.direction : 'left',
      closed: this.menuClosed,
    });
  }

  onMenuButtonClick(event: Event): void {
    this.menuPanel = !this.menuPanel;
    this.menuToggleClick.emit({
      direction: this.config ? this.config.direction : 'left',
      closed: this.menuPanel,
    });
    event.preventDefault();
  }

  showMenu(): void {
    if (this.menuClosed && this.menuPanel) {
      this.menuClosed = !this.menuClosed;
    }
  }
  hideMenu(): void {
    if (!this.menuClosed && this.menuPanel) {
      this.menuClosed = !this.menuClosed;
    }
  }

  removeUndefinedItems(arr) {
    return arr.map((obj) => {
      const newObj = { ...obj };
      if (newObj.items !== undefined && Array.isArray(newObj.items)) {
        newObj.items = this.removeUndefinedItems(newObj.items).filter(
          (item) => item !== undefined
        );
      } else {
        delete newObj.items;
      }
      return newObj;
    });
  }
}
